import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { Action, Selector, Store } from '@ngrx/store';
import { merge, Observable, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  startWith,
} from 'rxjs/operators';

import { QueryParamsModel } from '../../../core/_base/crud';
import { TableColumn, TableCustomFilter } from '../../models';
import { FormUtility } from '../../utils';
import { InputComponent } from '../input/input.component';

@Component({
  selector: 'kt-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions = new Subscription();
  private navigateToSubRoute: boolean;
  private itemsFilter: { [key: string]: any };
  items$: Observable<any>;
  totalCount$: Observable<number>;
  loading$: Observable<boolean>;
  columnsDef: string[] = [];
  filterColumnsDef: string[] = [];
  activeCustomFilterIdx: number;
  toggleableColumns: TableColumn[] = [];
  toggleTracker: { [key: string]: number } = {};
  searchControl = new FormControl('');
  showFilterRow = false;
  filterForm: FormGroup;
  filterFormDefaultValue: { [key: string]: any } = {};
  routeFilters: { [key: string]: any } = {};
  @ViewChild('searchInput', { static: true }) searchInput: InputComponent;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @Input() columns: TableColumn[] = [];
  @Input() customFilters: TableCustomFilter[] = [];
  @Input() routeFiltersKeys: string[] = [];
  @Input() pageSizeOptions = [10, 25, 50];
  @Input() defaultPageSize = 25;
  @Input() defaultSortDirection = 'desc';
  @Input() defaultSortActive = 'id';
  @Input() loadItemsAction: (args: { query: QueryParamsModel }) => Action;
  @Input() resetItemsAction: (args: {
    saveFilter: boolean;
    filter: { [key: string]: any };
  }) => Action;
  @Input() itemsSelector: Selector<any, any>;
  @Input() itemsFilterSelector: Selector<any, any>;
  @Input() loadingSelector: Selector<any, any>;
  @Input() hideFilters: boolean;
  @Input() hidePagination: boolean;

  constructor(
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formUtility: FormUtility,
  ) {}

  ngOnInit(): void {
    this.items$ = this.store.select(this.itemsSelector).pipe(map((s) => s.items));
    this.totalCount$ = this.store.select(this.itemsSelector).pipe(map((s) => s.totalCount));
    this.loading$ = this.store.select(this.loadingSelector);
    this.columnsDef = this.columns.map((c) => c.columnDef);
    this.filterColumnsDef = this.columns.map((c) => `${c.columnDef}Filter`);
    this.filterForm = new FormGroup({});

    this.subscriptions.add(
      this.store.select(this.itemsFilterSelector).subscribe((f) => {
        this.itemsFilter = f;
        this.sort.direction = this.itemsFilter?.direction;
        this.sort.active = this.itemsFilter?.active;
        this.paginator.pageIndex = this.itemsFilter?.pageIndex;
        this.paginator.pageSize = this.itemsFilter?.pageSize;
        this.showFilterRow = !!this.itemsFilter?.showFilterRow;
        this.searchControl.setValue(this.itemsFilter?.searchTerm || '');

        if (this.showFilterRow) {
          this.searchControl.disable();
        }

        this.columns.forEach((c) => {
          if (c.filterable) {
            this.filterForm.addControl(
              `${c.columnDef}Filter`,
              new FormControl(
                this.itemsFilter && this.itemsFilter[c.filterQueryParam]
                  ? this.itemsFilter[c.filterQueryParam]
                  : c.filterDefaultValue,
              ),
            );
            this.filterFormDefaultValue[`${c.columnDef}Filter`] = c.filterDefaultValue;
          }

          if (c.toggleable) {
            const toggleControl = this.getFilterFormControl(c.columnDef);

            this.toggleableColumns = [...this.toggleableColumns, c];
            this.toggleTracker[c.columnDef] =
              toggleControl?.value?.value === 'true'
                ? 0
                : toggleControl?.value?.value === 'false'
                ? 1
                : 2;
            this.subscriptions.add(
              toggleControl.valueChanges.subscribe(() =>
                this.handleToggleControlChange(c.columnDef),
              ),
            );
          }
        });
      }),
    );

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe((event: NavigationStart) => {
          const currentUrl = this.router.url;
          const targetUrl = event.url;

          this.navigateToSubRoute = targetUrl.startsWith(currentUrl);
        }),
    );

    this.subscriptions.add(
      this.sort.sortChange.subscribe((sort) => {
        this.paginator.pageIndex = 0;

        if (!sort?.direction) {
          this.sort.direction = '';
          this.sort.active = '';
        }
      }),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page).subscribe(() => this.loadItemsList()),
    );

    this.subscriptions.add(
      this.searchControl.valueChanges
        .pipe(startWith(''), debounceTime(500), distinctUntilChanged(), pairwise())
        .subscribe(([previous, current]) => {
          if (current !== previous) {
            this.reloadItemsList();
          }
        }),
    );

    setTimeout(() => {
      let useCustom: boolean;
      let customFilterIdx: number;
      let customFilter: TableCustomFilter;

      if (typeof this.itemsFilter?.activeCustomFilterIdx === 'number') {
        useCustom = true;
        customFilterIdx = this.itemsFilter?.activeCustomFilterIdx;
        customFilter = this.customFilters[customFilterIdx];
      } else {
        customFilter = this.customFilters.find((f, i) => {
          if (f.firstLoad) {
            customFilterIdx = i;
            return true;
          }

          return false;
        });

        useCustom = !!customFilter && !this.itemsFilter;
      }

      if (useCustom) {
        this.applyCustomFilter(customFilterIdx, customFilter, false);
      } else {
        this.loadItemsList();
      }
    });
  }

  ngAfterViewInit(): void {
    const buttons = this.paginator._intl as any;
    const pageSizeSelect = document.querySelector('.mat-form-field.mat-paginator-page-size-select');

    buttons.previousPageLabel = '';
    buttons.nextPageLabel = '';
    buttons.firstPageLabel = '';
    buttons.lastPageLabel = '';

    pageSizeSelect.classList.remove('mat-form-field-appearance-legacy');
    pageSizeSelect.classList.add(
      'ks-autocomplete-md',
      'ks-autocomplete-neutral',
      'ks-autocomplete-no-space',
      'mat-form-field-appearance-outline',
    );

    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe((params) => {
        this.routeFiltersKeys.forEach((k) => (this.routeFilters[k] = params[k]));
        this.loadItemsList();
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.resetItemsAction) {
      this.store.dispatch(
        this.resetItemsAction({
          saveFilter: this.navigateToSubRoute,
          filter: {
            ...this.getFilter(true),
            direction: this.sort.direction,
            active: this.sort.active,
            pageIndex: this.paginator.pageIndex,
            pageSize: this.paginator.pageSize,
            showFilterRow: this.showFilterRow,
            activeCustomFilterIdx: this.activeCustomFilterIdx,
          },
        }),
      );
    }

    this.subscriptions.unsubscribe();
  }

  getFilterFormControl(columnDef: string): FormControl {
    return this.formUtility.getFormControl(this.filterForm, `${columnDef}Filter`);
  }

  getFilter(useCtrlVal = false) {
    const filterFormVal = this.filterForm.value;
    const itemsFilter: { [key: string]: any } = {};

    itemsFilter.searchTerm = this.searchControl.value;

    this.columns.forEach((c) => {
      const filterCtrlVal = filterFormVal[`${c.columnDef}Filter`];

      if (c.filterable) {
        itemsFilter[c.filterQueryParam] =
          filterCtrlVal && c.filterObjectKey && !useCtrlVal
            ? Array.isArray(filterCtrlVal)
              ? filterCtrlVal.map((v) => v[c.filterObjectKey])
              : filterCtrlVal[c.filterObjectKey]
            : filterCtrlVal;
      }
    });

    Object.keys(this.routeFilters).forEach((k) => {
      itemsFilter[k] = this.routeFilters[k];
    });

    return itemsFilter;
  }

  resetFilter(custom: { [key: string]: any } = {}): void {
    const resetFilterValue = { ...this.filterFormDefaultValue };

    this.toggleableColumns.forEach(
      (c) =>
        (resetFilterValue[`${c.columnDef}Filter`] = this.getFilterFormControl(c.columnDef)?.value),
    );
    this.filterForm.reset({ ...resetFilterValue, ...custom });
  }

  toggleFilterRow(): void {
    this.resetFilter();
    this.showFilterRow = !this.showFilterRow;
    this.activeCustomFilterIdx = null;

    if (this.showFilterRow) {
      this.searchControl.setValue('');
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
      this.searchInput.focus();
      this.reloadItemsList();
    }
  }

  applyCustomFilter(
    customFilterIdx: number,
    customFilter: TableCustomFilter,
    resetPageIdx = true,
  ): void {
    const custom = {};

    customFilter.filter.forEach((f) => (custom[`${f.columnDef}Filter`] = f.value));

    this.showFilterRow = false;
    this.resetFilter(custom);

    if (resetPageIdx) {
      this.reloadItemsList(customFilterIdx);
    } else {
      this.loadItemsList(customFilterIdx);
    }
  }

  applyToggleFilter(columnDef: string): void {
    const toggleOptions = this.columns.find((c) => c.columnDef === columnDef)?.toggleOptions;
    const toggleControl = this.getFilterFormControl(columnDef);

    this.toggleTracker[columnDef] = (this.toggleTracker[columnDef] + 1) % 3;

    switch (this.toggleTracker[columnDef]) {
      case 0:
        toggleControl.setValue(toggleOptions.find((o) => o.value === 'true'));
        break;
      case 1:
        toggleControl.setValue(toggleOptions.find((o) => o.value === 'false'));
        break;
      default:
        toggleControl.setValue(toggleOptions.find((o) => o.value === ''));
    }

    this.reloadItemsList();
  }

  handleToggleControlChange(columnDef: string): void {
    switch (this.getFilterFormControl(columnDef)?.value?.value) {
      case 'true':
        this.toggleTracker[columnDef] = 0;
        break;
      case 'false':
        this.toggleTracker[columnDef] = 1;
        break;
      default:
        this.toggleTracker[columnDef] = 2;
    }
  }

  loadItemsList(customFilterIdx?: number): void {
    const query = new QueryParamsModel(
      this.getFilter(),
      this.sort.direction || this.defaultSortDirection,
      this.sort.active || this.defaultSortActive,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    if (typeof customFilterIdx === 'number') {
      this.activeCustomFilterIdx = customFilterIdx;
    }

    this.store.dispatch(this.loadItemsAction({ query }));
  }

  reloadItemsList(customFilterIdx?: number): void {
    this.paginator.pageIndex = 0;
    this.loadItemsList(customFilterIdx);
  }
}
